"use client"

import { useState, useEffect } from "react"
import { collection, query, onSnapshot, updateDoc, doc, deleteDoc, getDocs, serverTimestamp } from "firebase/firestore"
import { db, auth } from "@/lib/firebase"
import { Loader2, Trash2, Star, MessageSquare, BookOpen, Send, UserCheck, Pencil, Check, X, Eye, EyeOff } from "lucide-react"
import { toast } from "sonner"
import { onAuthStateChanged } from "firebase/auth"

interface Review {
  id: string
  courseId: string
  courseTitle?: string
  userId: string
  userName: string
  text: string
  rating: number
  adminReply?: string
  adminReplyAt?: any
  createdAt: any
  approved?: boolean
}

export default function ReviewsManager() {
  const [reviews, setReviews] = useState<Review[]>([])
  const [courses, setCourses] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [replyText, setReplyText] = useState<{ [key: string]: string }>({})
  const [authReady, setAuthReady] = useState(false)

  // ── Edit state ──────────────────────────────────────────────────────────────
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState("")
  const [editRating, setEditRating] = useState(5)

  // ── Edit reply state ─────────────────────────────────────────────────────────
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null)
  const [editReplyText, setEditReplyText] = useState("")

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        setAuthReady(true)
        user.getIdTokenResult().then(token => {
          console.log("✅ Auth ready. UID:", user.uid, "| Claims:", token.claims)
        })
      } else {
        setAuthReady(false)
        console.warn("⚠️ No authenticated user.")
      }
    })
    return () => unsub()
  }, [])

  useEffect(() => {
    const fetchCourses = async () => {
      const snap = await getDocs(collection(db, "courses"))
      setCourses(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    }
    fetchCourses()
  }, [])

  useEffect(() => {
    if (courses.length === 0) return

    setLoading(true)
    const allUnsubs: (() => void)[] = []

    courses.forEach(course => {
      const q = query(collection(db, "courses", course.id, "reviews"))
      const unsub = onSnapshot(q, (snap) => {
        const courseReviews = snap.docs.map(d => ({
          id: d.id,
          courseId: course.id,
          courseTitle: course.title,
          ...d.data()
        })) as Review[]

        setReviews(prev => {
          const otherReviews = prev.filter(r => r.courseId !== course.id)
          return [...otherReviews, ...courseReviews].sort(
            (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
          )
        })
      })
      allUnsubs.push(unsub)
    })

    setLoading(false)
    return () => allUnsubs.forEach(u => u())
  }, [courses])

  // ── Guard helper ─────────────────────────────────────────────────────────────
  const guardAuth = (): boolean => {
    if (!auth.currentUser) {
      toast.error("Session expired. Please log in again.")
      return false
    }
    return true
  }

  // ── Delete ───────────────────────────────────────────────────────────────────
  const toggleVisibility = async (review: Review) => {
    try {
      const newStatus = (review as any).isVisible === false ? true : false
      await updateDoc(doc(db, "courses", review.courseId, "reviews", review.id), {
        isVisible: newStatus
      })
      toast.success(newStatus ? "Review visible" : "Review hidden")
      setReviews(prev => prev.map(r => r.id === review.id ? { ...r, isVisible: newStatus } : r))
    } catch (error) {
      toast.error("Failed to update visibility")
    }
  }

  const deleteReview = async (review: Review) => {
    if (!confirm("Permanently delete this review?")) return
    if (!guardAuth()) return
    try {
      await deleteDoc(doc(db, "courses", review.courseId, "reviews", review.id))
      toast.success("Review deleted")
    } catch (error: any) {
      console.error("❌ Delete failed:", error)
      toast.error(error.code === "permission-denied"
        ? "Permission denied. Check Firestore rules."
        : `Failed: ${error.message}`)
    }
  }

  // ── Edit Review (text + rating) ──────────────────────────────────────────────
  const startEdit = (review: Review) => {
    setEditingId(review.id)
    setEditText(review.text)
    setEditRating(review.rating)
    setEditingReplyId(null)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditText("")
  }

  const saveEdit = async (review: Review) => {
    if (!editText.trim()) { toast.error("Review text cannot be empty."); return }
    if (!guardAuth()) return
    try {
      await updateDoc(doc(db, "courses", review.courseId, "reviews", review.id), {
        text: editText.trim(),
        rating: editRating,
      })
      toast.success("Review updated")
      cancelEdit()
    } catch (error: any) {
      console.error("❌ Edit failed:", error)
      toast.error(error.code === "permission-denied"
        ? "Permission denied. Check Firestore rules."
        : `Failed: ${error.message}`)
    }
  }

  // ── Reply ────────────────────────────────────────────────────────────────────
  const handleReplyReview = async (review: Review) => {
    const reply = replyText[review.id]
    if (!reply?.trim()) return
    if (!guardAuth()) return
    try {
      await updateDoc(doc(db, "courses", review.courseId, "reviews", review.id), {
        adminReply: reply.trim(),
        adminReplyAt: serverTimestamp(),
        approved: true,
      })
      toast.success("Reply posted successfully!")
      setReplyText(prev => { const n = { ...prev }; delete n[review.id]; return n })
    } catch (error: any) {
      console.error("❌ Reply failed:", error)
      toast.error(error.code === "permission-denied"
        ? "Permission denied. Verify your admin role in Firestore or the /admins collection."
        : `Failed: ${error.message}`)
    }
  }

  // ── Edit existing reply ──────────────────────────────────────────────────────
  const startEditReply = (review: Review) => {
    setEditingReplyId(review.id)
    setEditReplyText(review.adminReply || "")
    setEditingId(null)
  }

  const cancelEditReply = () => {
    setEditingReplyId(null)
    setEditReplyText("")
  }

  const saveEditReply = async (review: Review) => {
    if (!editReplyText.trim()) { toast.error("Reply cannot be empty."); return }
    if (!guardAuth()) return
    try {
      await updateDoc(doc(db, "courses", review.courseId, "reviews", review.id), {
        adminReply: editReplyText.trim(),
        adminReplyAt: serverTimestamp(),
      })
      toast.success("Reply updated")
      cancelEditReply()
    } catch (error: any) {
      console.error("❌ Edit reply failed:", error)
      toast.error(error.code === "permission-denied"
        ? "Permission denied. Check Firestore rules."
        : `Failed: ${error.message}`)
    }
  }

  const deleteReply = async (review: Review) => {
    if (!confirm("Remove the official reply from this review?")) return
    if (!guardAuth()) return
    try {
      await updateDoc(doc(db, "courses", review.courseId, "reviews", review.id), {
        adminReply: null,
        adminReplyAt: null,
      })
      toast.success("Reply removed")
    } catch (error: any) {
      console.error("❌ Delete reply failed:", error)
      toast.error(error.code === "permission-denied"
        ? "Permission denied."
        : `Failed: ${error.message}`)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-serif font-bold text-slate-900">Student Reviews</h2>
        <p className="text-xs text-slate-500 uppercase tracking-widest font-bold mt-1">
          Manage feedback and provide official responses
        </p>
      </div>

      {loading && reviews.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-[#b48c36]">
          <Loader2 className="animate-spin mb-4" size={32} />
          <p className="text-[10px] font-bold uppercase tracking-[0.3em]">Querying Registry...</p>
        </div>
      ) : reviews.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 text-center">
          <MessageSquare className="mx-auto text-slate-200 mb-4" size={48} />
          <p className="text-slate-500 font-serif">No reviews found yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {reviews.map((review) => (
            <div key={review.id} className="bg-white border border-slate-200 p-6 flex flex-col justify-between transition-all hover:shadow-md">
              <div className="space-y-4">

                {/* ── Header row ── */}
                <div className="flex items-center justify-between">
                  <span className="bg-slate-950 text-[#b48c36] px-2 py-1 text-[9px] font-bold uppercase tracking-wider flex items-center gap-1.5">
                    <BookOpen size={10} /> {review.courseTitle || "Unknown"}
                  </span>
                  {editingId === review.id ? (
                    /* Star rating editor */
                    <div className="flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, j) => (
                        <button key={j} onClick={() => setEditRating(j + 1)}>
                          <Star
                            size={14}
                            className={j < editRating ? "fill-[#b48c36] text-[#b48c36]" : "text-slate-300"}
                          />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, j) => (
                        <Star key={j} size={10} className={j < review.rating ? "fill-[#b48c36] text-[#b48c36]" : "text-slate-200"} />
                      ))}
                    </div>
                  )}
                </div>

                {/* ── Review body ── */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 bg-slate-100 text-[#b48c36] flex items-center justify-center text-[10px] font-bold font-serif">
                        {review.userName?.charAt(0).toUpperCase()}
                      </div>
                      <p className="text-[10px] font-bold text-slate-900 uppercase tracking-widest">{review.userName}</p>
                    </div>

                    {editingId === review.id ? (
                      <textarea
                        rows={3}
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full bg-slate-50 border border-[#b48c36] p-3 text-sm focus:outline-none"
                        autoFocus
                      />
                    ) : (
                      <p className="text-sm text-slate-600 leading-relaxed font-sans ">"{review.text}"</p>
                    )}
                  </div>

                  {/* ── Action buttons ── */}
                  <div className="flex flex-col gap-1 shrink-0">
                    {editingId === review.id ? (
                      <>
                        <button
                          onClick={() => saveEdit(review)}
                          className="p-2 text-green-600 hover:text-green-700 transition-colors"
                          title="Save changes"
                        >
                          <Check size={18} />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                          title="Cancel"
                        >
                          <X size={18} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEdit(review)}
                          className="p-2 text-slate-400 hover:text-[#b48c36] transition-colors"
                          title="Edit Review"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => toggleVisibility(review)}
                          className={`p-2 transition-colors ${(review as any).isVisible === false ? 'text-rose-500' : 'text-slate-400 hover:text-emerald-500'}`}
                          title={(review as any).isVisible === false ? "Show Review" : "Hide Review"}
                        >
                          {(review as any).isVisible === false ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                        <button
                          onClick={() => deleteReview(review)}
                          className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                          title="Delete Review"
                        >
                          <Trash2 size={18} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* ── Admin reply area ── */}
                {review.adminReply ? (
                  <div className="bg-slate-50 p-4 border-l-2 border-[#b48c36] mt-4">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <UserCheck size={12} className="text-[#b48c36]" />
                        <p className="text-[9px] font-bold text-[#b48c36] uppercase tracking-widest">Institution Response</p>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => startEditReply(review)}
                          className="p-1 text-slate-400 hover:text-[#b48c36] transition-colors"
                          title="Edit reply"
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          onClick={() => deleteReply(review)}
                          className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                          title="Remove reply"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>

                    {editingReplyId === review.id ? (
                      <div className="flex gap-2 mt-2">
                        <textarea
                          rows={2}
                          value={editReplyText}
                          onChange={(e) => setEditReplyText(e.target.value)}
                          className="flex-1 bg-white border border-[#b48c36] p-2 text-sm focus:outline-none"
                          autoFocus
                        />
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => saveEditReply(review)}
                            className="p-2 text-green-600 hover:text-green-700"
                            title="Save reply"
                          >
                            <Check size={14} />
                          </button>
                          <button
                            onClick={cancelEditReply}
                            className="p-2 text-slate-400 hover:text-slate-600"
                            title="Cancel"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-700 leading-relaxed font-sans">{review.adminReply}</p>
                    )}
                  </div>
                ) : (
                  <div className="mt-4 pt-4 border-t border-slate-50 flex gap-3">
                    <textarea
                      rows={2}
                      value={replyText[review.id] || ""}
                      onChange={(e) => setReplyText(prev => ({ ...prev, [review.id]: e.target.value }))}
                      placeholder="Write an official response..."
                      className="flex-1 bg-slate-50 border border-slate-200 p-3 text-sm focus:outline-none focus:border-[#b48c36] placeholder:text-slate-400"
                    />
                    <button
                      onClick={() => handleReplyReview(review)}
                      disabled={!authReady}
                      className="bg-slate-950 text-white px-5 flex items-center justify-center transition-colors hover:bg-[#b48c36] disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Send size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
